import {fromSeconds} from 'common/date';
import SharePrice from 'common/SharePrice';
import type {EquityIssuersQuery} from '@transactions/graphql';

/**
 * Series computation for the equity dashboard — the client-side counterpart
 * of the server's replay math. Prices arrive as decimal strings + currency
 * and are parsed ONCE here into SharePrice (the designed boundary); every
 * dollar figure below comes out of `.times() → Money` in integer cents.
 *
 * Three lines, from the same ledger:
 * - neverSold: every share ever received — including shares withheld for
 *   taxes at settlement — valued at the price of the day. The pure-HODL
 *   counterfactual. (Certificate history only: pre-settlement vesting
 *   lives in the workspace archive, not the database, so this line starts
 *   at the first lot rather than the 2016 option cliff.)
 * - realizedHeld: held value plus gross cash banked at each sale plus the
 *   value of tax-withheld shares at their settlement date. Continuous
 *   through sales by construction — value converts, never vanishes.
 * - actual: shares currently held, valued at the price of the day.
 *
 * The price-basis toggle revalues the HELD portion at today's latest mark
 * instead of the mark of the day; realized amounts stay frozen at what was
 * actually banked.
 *
 * Vested-unexercised OPTIONS count as holdings at intrinsic value —
 * max(price − strike, 0) × (vested tranches minus attributed exercises) —
 * on every line: they were real value between vest and exercise (Dan's
 * 2016–2023 window). At exercise the lot takes over at full price; the
 * step up by the strike paid is the cash injected, not a discontinuity.
 */

export type EquityIssuerData = EquityIssuersQuery['equityIssuers'][number];
export type EquityGrantData = EquityIssuerData['grants'][number];
export type EquityMarkData = EquityIssuerData['marks'][number];
export type EquityLotData = EquityIssuerData['lots'][number];
export type EquityEventData = EquityIssuerData['events'][number];

export type PriceBasis = 'atTheTime' | 'today';

export type EquityPoint = {
  /** Milliseconds, midnight UTC — the charts' shared x. */
  timestamp: number;
  /** Dollars (floats only at the chart boundary; math happens in cents). */
  neverSold: number;
  realizedHeld: number;
  actual: number;
  /** Dollars per share at this date (the actual mark, never the toggle). */
  fmv: number | null;
  /** Present on dates where a sale happened — renders the badge dot. */
  sale: {name: string; shares: number; grossCents: number | null} | null;
};

const toMs = (seconds: number): number => seconds * 1000;

/** Marks sorted ascending with prices parsed once. */
const parsedMarks = (
  issuer: EquityIssuerData,
): Array<{date: number; price: SharePrice}> =>
  [...issuer.marks]
    .sort((a, b) => a.date - b.date)
    .map((mark) => ({
      date: mark.date,
      price: SharePrice.parse(mark.price, mark.currency),
    }));

const markAt = (
  marks: Array<{date: number; price: SharePrice}>,
  date: number,
): SharePrice | null => {
  let current: SharePrice | null = null;
  for (const mark of marks) {
    if (mark.date <= date) current = mark.price;
    else break;
  }
  return current;
};

/**
 * Latest mark as of `todaySeconds` — never a future-dated one, matching the
 * server's own current-valuation rule (a future mark would contradict the
 * issuer's `valueCents` on the same page).
 */
export const latestMark = (
  issuer: EquityIssuerData,
  todaySeconds: number,
): {date: number; price: SharePrice} | null => {
  const marks = parsedMarks(issuer).filter((mark) => mark.date <= todaySeconds);
  return marks.length > 0 ? marks[marks.length - 1] : null;
};

/**
 * The display currency for the issuer's cent amounts. Documents carry their
 * own currency (validated ISO-4217 and issuer-consistent server-side), so
 * any one of them speaks for the issuer.
 */
export const issuerCurrency = (issuer: EquityIssuerData): string =>
  issuer.marks[0]?.currency ?? issuer.lots[0]?.currency ?? 'USD';

/** Integer cents to a localized amount in the issuer's currency. */
export const formatCents = (value: number, currency: string): string =>
  (value / 100).toLocaleString('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  });

export const lotBasis = (lot: EquityLotData): SharePrice =>
  SharePrice.parse(lot.basisPerShare, lot.currency);

/**
 * Build the chart series. Points sit at every date the ledger knows about
 * (marks, lot acquisitions, events) plus `todaySeconds`, so step rendering
 * reproduces the exact replay the server would compute.
 */
export const buildEquitySeries = (
  issuer: EquityIssuerData,
  {priceBasis, todaySeconds}: {priceBasis: PriceBasis; todaySeconds: number},
): EquityPoint[] => {
  const marks = parsedMarks(issuer);
  if (marks.length === 0) return [];
  const latest = latestMark(issuer, todaySeconds)?.price ?? null;
  const lots = [...issuer.lots].sort((a, b) => a.acquiredDate - b.acquiredDate);
  const events = [...issuer.events].sort((a, b) => a.date - b.date);
  // Proceeds an event banked, best-effort when undocumented: the stated
  // gross, else price x shares, else the mark of the day x shares. Without
  // the fallback an estimated sale would subtract its shares from the
  // realized+held line while adding nothing back — a permanent understatement
  // masquerading as continuity.
  const proceedsCents = (event: EquityEventData): number => {
    if (event.grossCents != null) return event.grossCents;
    // A sale before the first mark still estimates at the earliest known
    // price rather than 0 — same best-effort rule as withheld settlements.
    const price =
      event.price != null
        ? SharePrice.parse(event.price, event.currency)
        : (markAt(marks, event.date) ?? marks[0].price);
    return price.times(event.sharesSold).amount;
  };
  const eventsByDate = new Map<number, EquityEventData[]>();
  for (const event of events) {
    const list = eventsByDate.get(event.date) ?? [];
    list.push(event);
    eventsByDate.set(event.date, list);
  }

  // Option grants: strike parsed once, tranches sorted; exercises attribute
  // through lot.grantId (shares + withheld both came out of the grant).
  const grants = issuer.grants.map((grant) => ({
    id: grant._id,
    strike: SharePrice.parse(grant.strikePerShare, grant.currency),
    tranches: [...grant.tranches].sort((a, b) => a.date - b.date),
  }));
  const exercisesByGrant = new Map<string, Array<{date: number; shares: number}>>();
  for (const lot of lots) {
    if (lot.grantId == null) continue;
    const list = exercisesByGrant.get(lot.grantId) ?? [];
    list.push({
      date: lot.acquiredDate,
      shares: lot.sharesAcquired + (lot.sharesWithheldForTax ?? 0),
    });
    exercisesByGrant.set(lot.grantId, list);
  }
  /** Intrinsic value of vested-unexercised options at `date`, priced at `price`. */
  const optionValueCents = (date: number, price: SharePrice | null): number => {
    if (price == null) return 0;
    let cents = 0;
    for (const grant of grants) {
      // Cross-currency strike vs mark cannot be compared; such a grant
      // contributes nothing rather than throwing (unlinked-issuer armor).
      if (grant.strike.currency !== price.currency) continue;
      if (price.compare(grant.strike) <= 0) continue;
      const vested = grant.tranches.reduce(
        (sum, tranche) => (tranche.date <= date ? sum + tranche.shares : sum),
        0,
      );
      const exercised = (exercisesByGrant.get(grant.id) ?? []).reduce(
        (sum, exercise) =>
          exercise.date <= date ? sum + exercise.shares : sum,
        0,
      );
      const outstanding = Math.max(0, vested - exercised);
      if (outstanding === 0) continue;
      cents += price.minus(grant.strike).times(outstanding).amount;
    }
    return cents;
  };

  // The chart spans first mark → today: pre-mark dates would plot unknown
  // values as $0 (the server models them as null), and future-dated
  // documents (the mutations accept them) stay off like the server's
  // current valuation.
  const firstMarkDate = marks[0].date;
  const dates = [
    ...new Set([
      ...marks.map((mark) => mark.date),
      ...lots.map((lot) => lot.acquiredDate),
      ...events.map((event) => event.date),
      ...grants.flatMap((grant) =>
        grant.tranches.map((tranche) => tranche.date),
      ),
      todaySeconds,
    ]),
  ]
    .filter((date) => date >= firstMarkDate && date <= todaySeconds)
    .sort((a, b) => a - b);

  return dates.map((date) => {
    let acquired = 0;
    let withheld = 0;
    // Withheld shares settled a tax bill: their value freezes at the mark
    // of their settlement date, feeding the realized line.
    let withheldValueCents = 0;
    for (const lot of lots) {
      if (lot.acquiredDate > date) break;
      acquired += lot.sharesAcquired;
      const lotWithheld = lot.sharesWithheldForTax ?? 0;
      if (lotWithheld > 0) {
        withheld += lotWithheld;
        // The lot's basis IS the settlement-date FMV (the model defines
        // basisPerShare as FMV at acquisition for releases and exercises),
        // so it beats any surrounding mark — a preceding one is stale and
        // a later one is look-ahead.
        withheldValueCents += lotBasis(lot).times(lotWithheld).amount;
      }
    }
    let sold = 0;
    let grossCents = 0;
    for (const event of events) {
      if (event.date > date) break;
      sold += event.sharesSold;
      grossCents += proceedsCents(event);
    }

    const priceOfDay = markAt(marks, date);
    const heldPrice = priceBasis === 'today' ? (latest ?? priceOfDay) : priceOfDay;
    const value = (shares: number): number =>
      heldPrice ? heldPrice.times(shares).amount / 100 : 0;

    const daySales = eventsByDate.get(date) ?? [];
    const sale =
      daySales.length > 0
        ? {
            name: daySales.map((event) => event.name).join(' · '),
            shares: daySales.reduce(
              (sum, event) => sum + event.sharesSold,
              0,
            ),
            // Partial documentation makes the day's total unknown — a sum
            // over some of the sales would understate it as if complete.
            grossCents: daySales.some((event) => event.grossCents == null)
              ? null
              : daySales.reduce((sum, event) => sum + (event.grossCents ?? 0), 0),
          }
        : null;

    // Options are holdings too: intrinsic value rides every line, priced
    // like the shares (so the today-basis toggle revalues them as well).
    const optionCents = optionValueCents(date, heldPrice);
    return {
      timestamp: toMs(date),
      neverSold: value(acquired + withheld) + optionCents / 100,
      actual: value(acquired - sold) + optionCents / 100,
      realizedHeld:
        value(acquired - sold) +
        (grossCents + withheldValueCents + optionCents) / 100,
      fmv: priceOfDay ? priceOfDay.toNumber() : null,
      sale,
    };
  });
};

export type LotRow = {
  lot: EquityLotData;
  basis: SharePrice;
  /** Shares still held as of today — future-dated event draws excluded. */
  sharesRemaining: number;
  basisCents: number;
  valueCents: number | null;
  unrealizedCents: number | null;
  longTerm: boolean;
};

/**
 * Per-lot figures for the holdings table, latest-mark valued, as of today:
 * the stored `sharesRemaining` nets out every event including future-dated
 * ones, so draws are re-derived from events through `todaySeconds` (and
 * future-dated lots are omitted) to match the issuer's own `sharesHeld`.
 */
export const buildLotRows = (
  issuer: EquityIssuerData,
  todaySeconds: number,
): LotRow[] => {
  const latest = latestMark(issuer, todaySeconds);
  // Long-term means held MORE than one calendar year: acquired strictly
  // before today's one-year-ago anniversary (365-day math drifts across
  // leap years, and the anniversary itself is still short-term).
  const oneYearAgo = fromSeconds(todaySeconds).minus({years: 1}).toSeconds();
  const drawnByLot = new Map<string, number>();
  for (const event of issuer.events) {
    if (event.date > todaySeconds) continue;
    for (const draw of event.draws) {
      drawnByLot.set(
        draw.lotId,
        (drawnByLot.get(draw.lotId) ?? 0) + draw.shares,
      );
    }
  }
  return issuer.lots
    .filter((lot) => lot.acquiredDate <= todaySeconds)
    .sort((a, b) => a.acquiredDate - b.acquiredDate)
    .map((lot) => {
      const basis = lotBasis(lot);
      const remaining = lot.sharesAcquired - (drawnByLot.get(lot._id) ?? 0);
      // Cross-currency documents are storable on an unlinked issuer;
      // SharePrice.minus throws on a mismatch, so an incomparable pair
      // reads as unknown rather than crashing the page.
      const comparable =
        latest != null && latest.price.currency === basis.currency;
      return {
        lot,
        basis,
        sharesRemaining: remaining,
        basisCents: basis.times(remaining).amount,
        valueCents: latest ? latest.price.times(remaining).amount : null,
        unrealizedCents: comparable
          ? latest.price.minus(basis).times(remaining).amount
          : null,
        longTerm: lot.acquiredDate < oneYearAgo,
      };
    });
};

export type IssuerTotals = {
  sharesHeld: number;
  valueCents: number | null;
  basisCents: number;
  unrealizedCents: number | null;
};

export const buildTotals = (
  issuer: EquityIssuerData,
  todaySeconds: number,
): IssuerTotals => {
  const rows = buildLotRows(issuer, todaySeconds).filter(
    (row) => row.sharesRemaining > 0,
  );
  // The aggregate is unknown as soon as any held row's gain is — a partial
  // sum in the tile would contradict the em dash in the lots footer.
  const allKnown = rows.every((row) => row.unrealizedCents != null);
  return {
    sharesHeld: issuer.sharesHeld,
    valueCents: issuer.valueCents ?? null,
    basisCents: rows.reduce((sum, row) => sum + row.basisCents, 0),
    unrealizedCents: allKnown
      ? rows.reduce((sum, row) => sum + (row.unrealizedCents ?? 0), 0)
      : null,
  };
};

export type SaleTotals = {
  eventCount: number;
  sharesSold: number;
  /** Sum of documented grosses — the pre-tax total; null when none are documented. */
  grossCents: number | null;
  /** Sum of documented nets (after exercise costs and withholding); null when none. */
  netCents: number | null;
  /** Shares in the documented same-currency events — the divisor behind
   * grossCents and avgPriceDollars; differs from sharesSold when events
   * are excluded. */
  documentedSharesSold: number;
  /** Gross ÷ documented shares, in dollars per share. */
  avgPriceDollars: number | null;
  /** Documented per-share price range across events, dollars. */
  minPriceDollars: number | null;
  maxPriceDollars: number | null;
  /** Events whose gross is unknown — excluded from every sum above. */
  undocumentedCount: number;
  /** Documented events in another currency — also excluded, but known. */
  excludedCurrencyCount: number;
  /** Same-currency events with neither gross nor net — absent from netCents. */
  netUnknownCount: number;
  /** Events whose figures are estimates (documents lost). */
  estimatedCount: number;
};

/**
 * Lifetime sale totals, as of today. Gross is the anchor number — the
 * pre-tax sale price, straight from the documents — so events with no
 * recorded gross are excluded from the sums and counted instead, never
 * summed as zero.
 */
export const buildSaleTotals = (
  issuer: EquityIssuerData,
  todaySeconds: number,
): SaleTotals => {
  const currency = issuerCurrency(issuer);
  const events = issuer.events.filter((event) => event.date <= todaySeconds);
  // Countable = has a gross AND is in the issuer's display currency —
  // cross-currency cents (storable on an unlinked issuer) can no more be
  // summed than compared, so they join the excluded count instead.
  const documented = events.filter(
    (event) => event.grossCents != null && event.currency === currency,
  );
  const grossCents =
    documented.length > 0
      ? documented.reduce((sum, event) => sum + (event.grossCents ?? 0), 0)
      : null;
  const documentedShares = documented.reduce(
    (sum, event) => sum + event.sharesSold,
    0,
  );
  // Range and average sit in one tile, so both describe the same events:
  // the documented ones. Endpoints derive as gross over shares when there
  // is no recorded price — or when the event is estimated, whose price and
  // gross are allowed to disagree and whose gross is what the average uses.
  const prices = documented
    .map((event) =>
      event.price != null && !event.estimated
        ? SharePrice.parse(event.price, event.currency).toNumber()
        : event.sharesSold > 0
          ? (event.grossCents ?? 0) / 100 / event.sharesSold
          : null,
    )
    .filter((price): price is number => price != null);
  return {
    eventCount: events.length,
    sharesSold: events.reduce((sum, event) => sum + event.sharesSold, 0),
    grossCents,
    documentedSharesSold: documentedShares,
    // Net counts every same-currency event that pins it down: a recorded
    // net (even with no gross), else gross minus recorded costs — the
    // waterfall's own derivation, or gross would masquerade as cash in hand.
    netCents: (() => {
      const knownNet = events.filter(
        (event) =>
          event.currency === currency &&
          (event.netCents != null || event.grossCents != null),
      );
      return knownNet.length > 0
        ? knownNet.reduce(
            (sum, event) =>
              sum +
              (event.netCents ??
                (event.grossCents ?? 0) -
                  event.withholdingCents -
                  event.exerciseCostCents),
            0,
          )
        : null;
    })(),
    avgPriceDollars:
      grossCents != null && documentedShares > 0
        ? grossCents / 100 / documentedShares
        : null,
    minPriceDollars: prices.length > 0 ? Math.min(...prices) : null,
    maxPriceDollars: prices.length > 0 ? Math.max(...prices) : null,
    // The two exclusion counts partition the excluded events: any other-
    // currency event is a currency exclusion whatever fields it carries;
    // "undocumented" means same-currency with no recorded gross.
    undocumentedCount: events.filter(
      (event) => event.currency === currency && event.grossCents == null,
    ).length,
    excludedCurrencyCount: events.filter(
      (event) => event.currency !== currency,
    ).length,
    netUnknownCount: events.filter(
      (event) =>
        event.currency === currency &&
        event.grossCents == null &&
        event.netCents == null,
    ).length,
    estimatedCount: events.filter((event) => event.estimated).length,
  };
};
